import { Alert, Flex, Text } from "@/atoms"
import { isUnhealthy } from "@/domain/integration"
import { isVendorTicketingSystem } from "@/domain/ticketing"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"
import { TicketingDisconnectButton } from "./TicketingDisconnectButton"
import { TicketingNotificationForm } from "./TicketingNotificationForm"
import { getTicketingSettings } from "./ticketingSettings.selector"

import type { TicketingSystem } from "@/domain/ticketing"
import type { TicketingConfigurationTabProps } from "./TicketingConfigurationTab.types"

// Jira keeps its own texts; the other vendors share one set naming the vendor
const getKeyPrefix = (vendor: TicketingSystem) =>
  vendor === "jira"
    ? "integrations.configuration.jira"
    : "integrations.configuration.ticketing"

export const TicketingConfigurationTab = (
  props: TicketingConfigurationTabProps
) => {
  const { vendor } = props
  const keyPrefix = getKeyPrefix(vendor)
  const { t } = useTranslation("translation", { keyPrefix })
  const { t: tSystems } = useTranslation("translation", {
    keyPrefix: "ticketing.systems"
  })
  const [data, { isPending, onReset, isDirty, t: _t, ...rest }] =
    useIntegration(props)
  const { hasPermission } = useUser()
  const { id, name, account, health, notificationSettings } =
    getTicketingSettings(data?.configuration, vendor)
  const editDisabled = !hasPermission({ action: "*", resource: "*" })
  const vendorName = tSystems(vendor)

  return (
    <Flex vertical gap={16}>
      {account && <Text type="secondary">{t("account", { account })}</Text>}
      {isUnhealthy(health) && !!health.message && (
        <Alert
          showIcon
          type={health.status === "unhealthy" ? "error" : "warning"}
          title={health.message}
        />
      )}
      <TicketingNotificationForm
        id={id}
        name={name}
        settings={notificationSettings}
        keyPrefix={keyPrefix}
        vendorName={vendorName}
        isDirty={isDirty}
        isPending={isPending}
        isDisabled={editDisabled}
        onReset={onReset}
        footerStart={
          isVendorTicketingSystem(vendor) && (
            <TicketingDisconnectButton
              vendor={vendor}
              vendorName={vendorName}
            />
          )
        }
        {...rest}
      />
    </Flex>
  )
}
